import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
  useMotionValueEvent,
  useReducedMotion,
  useInView,
  animate,
} from 'motion/react';
import {
  PlugsConnected,
  Receipt,
  Play,
  Lightning,
  ShieldCheck,
  ChartLineUp,
  ArrowUpRight,
  ArrowRight,
  Check,
  CaretDown,
  SquaresFour,
  UserFocus,
  List,
  X,
} from '@phosphor-icons/react';
import mahomesVid from './assets/mahomes.mp4';
import lebronVid  from './assets/lebron.mp4';
import cmcVid     from './assets/cmc.mp4';
import eaglesVid  from './assets/eagles.mp4';

// Where every CTA on the site sends people
const APP_URL = 'https://app.myzonesports.com';

// ── Media query hook (for pinned vs stacked layouts) ────────
function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    mq.addEventListener('change', onChange);
    setMatches(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

// ── Shared scroll-reveal wrapper ────────────────────────────
function Reveal({ children, delay = 0, y = 28, className = '' }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ── Launch-app CTA (navbar, hero, bottom CTA) ───────────────
function LaunchAppButton({ className = '', label = 'Launch the App' }) {
  return (
    <a
      className={`btn-primary btn-launch${className ? ` ${className}` : ''}`}
      href={APP_URL}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
      <ArrowUpRight size={16} weight="bold" />
    </a>
  );
}

// ── Navbar: floating glass pill ─────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, 'change', (v) => {
    const next = v > 24;
    setScrolled(prev => (prev === next ? prev : next));
  });

  const scrollTo = (id) => {
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <motion.header
      className="nav-wrap"
      initial={reduce ? false : { y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
    >
      <nav className={`navbar${scrolled ? ' navbar--scrolled' : ''}`}>
        <button className="navbar__logo" onClick={() => scrollTo('hero')}>
          <img src="/MyZone.PNG" alt="" className="navbar__logo-img" />
          <span className="navbar__logo-text">MyZone<span className="navbar__logo-accent"> Sports</span></span>
        </button>

        <ul className={`navbar__links${menuOpen ? ' navbar__links--open' : ''}`}>
          <li><button onClick={() => scrollTo('how-it-works')}>How It Works</button></li>
          <li><button onClick={() => scrollTo('features')}>Features</button></li>
          <li><button onClick={() => scrollTo('press')}>Press</button></li>
          <li><button onClick={() => scrollTo('our-story')}>Our Story</button></li>
        </ul>

        <LaunchAppButton className="navbar__cta" label="Launch App" />

        <button
          className="navbar__burger"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          {menuOpen ? <X size={22} /> : <List size={22} />}
        </button>
      </nav>
    </motion.header>
  );
}

// ── Hero demo data ──────────────────────────────────────────
const HERO_SCENARIOS = [
  {
    sport: 'NFL',
    sportColor: '#e74c3c',
    video: mahomesVid,
    teams: ['KC', 'BAL'],
    scores: ['20', '10'],
    quarter: 'Q3 11:56',
    alert: 'Mahomes 12-yd TD pass',
    tracker: { label: 'Fantasy Tracker', name: 'P. Mahomes', detail: 'QB · Chiefs', stat: '+4.48 pts', statColor: '#34d399' },
  },
  {
    sport: 'NBA',
    sportColor: '#60a5fa',
    video: lebronVid,
    teams: ['LAL', 'ATL'],
    scores: ['92', '63'],
    quarter: 'Q3 8:06',
    alert: 'LeBron with back-to-back buckets',
    tracker: { label: 'Fantasy Tracker', name: 'LeBron James', detail: 'SF · Lakers', stat: '+8 pts', statColor: '#34d399' },
  },
  {
    sport: 'NFL',
    sportColor: '#e74c3c',
    video: cmcVid,
    teams: ['SF', 'CAR'],
    scores: ['17', '9'],
    quarter: 'Q4 15:00',
    alert: 'McCaffrey approaching rush yards prop',
    tracker: { label: 'Bet Slip Tracker', name: 'McCaffrey Over 85.5 Rush Yds', detail: '71 yds · 14.5 to go', stat: 'On Track', statColor: '#fbbf24' },
  },
  {
    sport: 'PARLAY',
    sportColor: '#a78bfa',
    video: eaglesVid,
    teams: ['PHI', 'KC'],
    scores: ['20', '10'],
    quarter: 'Q4 3:07',
    alert: 'Last leg live: Eagles need to hold',
    parlayLegs: [
      { label: 'Chiefs ML', status: 'win' },
      { label: 'LeBron 25+ pts', status: 'win' },
      { label: 'Eagles -2.5', status: 'live' },
    ],
  },
];

// ── Hero: copy + large 3D-tilted product demo frame ─────────
function Hero() {
  const reduce = useReducedMotion();
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const [active, setActive] = useState(0);
  const [switching, setSwitching] = useState(false);

  // 3D tilt: rests at a slight angle, follows the mouse
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 70, damping: 18 });
  const smy = useSpring(my, { stiffness: 70, damping: 18 });
  const rotY = useTransform(smx, v => -7 + v * 7);
  const rotX = useTransform(smy, v => 4 - v * 6);

  const tilted = isDesktop && !reduce;

  const onPointerMove = (e) => {
    if (!tilted || e.pointerType !== 'mouse') return;
    const r = sectionRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const handleVideoEnd = () => {
    setSwitching(true);
    setTimeout(() => {
      setActive(a => (a + 1) % HERO_SCENARIOS.length);
      setSwitching(false);
    }, 450);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.load();
    v.play().catch(() => {});
  }, [active]);

  const g = HERO_SCENARIOS[active];
  const isParlay = Boolean(g.parlayLegs);

  const enter = (delay) => ({
    initial: reduce ? false : { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section id="hero" className="hero" ref={sectionRef} onPointerMove={onPointerMove}>
      <div className="hero-bg" aria-hidden="true">
        <div className="hero-grid" />
        <div className="aurora aurora-1" />
        <div className="aurora aurora-2" />
        <div className="hero-beam" />
      </div>

      <div className="hero__inner">
        <div className="hero__left">
          <motion.div className="hero__eyebrow" {...enter(0.3)}>
            <span className="hero__eyebrow-live">
              <span className="pulse-dot" />
              Now Live
            </span>
            <span className="hero__eyebrow-rule" aria-hidden="true" />
            <span className="hero__eyebrow-note">
              We just launched! <strong>Get 3 months free</strong> for a limited time
            </span>
          </motion.div>

          <h1 className="hero__headline">
            <span className="hero__line-mask"><motion.span className="hero__line" {...enter(0.4)}>Your Fantasy.</motion.span></span>
            <span className="hero__line-mask"><motion.span className="hero__line" {...enter(0.5)}>Your Bets.</motion.span></span>
            <span className="hero__line-mask"><motion.span className="hero__line gradient-text" {...enter(0.6)}>One Live Feed.</motion.span></span>
          </h1>

          <motion.p className="hero__sub" {...enter(0.72)}>
            Import your fantasy leagues and bet slips. MyZone switches your feed
            to the game that matters most, live.
          </motion.p>

          <motion.div className="hero__cta" {...enter(0.84)}>
            <LaunchAppButton className="btn-launch--hero" />
            <button className="btn-ghost" onClick={() => {
              const el = document.getElementById('how-it-works');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}>
              See How It Works
            </button>
          </motion.div>
        </div>

        <motion.div className="hero__right" {...enter(0.5)}>
          <div className="demo-persp">
            <motion.div
              className="demo-tilt"
              style={tilted ? { rotateY: rotY, rotateX: rotX, transformPerspective: 1300 } : undefined}
            >
              <div className="demo-frame">
                <div className="demo-frame__bar">
                  <div className="demo-frame__brand">
                    <img src="/MyZone.PNG" alt="" className="demo-frame__logo" />
                    MyZone Sports
                  </div>
                </div>

                <div className={`demo-frame__screen${switching ? ' demo--exit' : ''}`}>
                  <video
                    ref={videoRef}
                    key={active}
                    src={g.video}
                    autoPlay
                    muted
                    playsInline
                    disablePictureInPicture
                    onEnded={handleVideoEnd}
                    onContextMenu={e => e.preventDefault()}
                    className="demo-frame__video"
                  />
                  <div className="demo-frame__scanlines" aria-hidden="true" />
                  <div className="demo-frame__vignette" aria-hidden="true" />

                  <div className="demo-bug">
                    <span className="demo-bug__live">
                      <span className="pulse-dot" />
                      LIVE
                      <span className="demo-bug__tip">Demo footage. Not actually live.</span>
                    </span>
                    <span className="demo-bug__sport" style={{ color: g.sportColor }}>{g.sport}</span>
                    <span className="demo-bug__score">{g.teams[0]} {g.scores[0]} - {g.scores[1]} {g.teams[1]}</span>
                    <span className="demo-bug__clock">{g.quarter}</span>
                  </div>

                  <div className="hud-pill demo-frame__pill" style={{ background: `${g.sportColor}dd` }}>
                    <ArrowRight size={11} weight="bold" />
                    Auto-switching
                  </div>
                </div>

                <div className={`demo-frame__alert${switching ? ' demo--exit' : ''}`}>
                  <Lightning size={14} weight="fill" color={g.sportColor} />
                  {g.alert}
                </div>

                <div className="demo-frame__dots" aria-hidden="true">
                  {HERO_SCENARIOS.map((s, i) => (
                    <span key={i} className={`demo-dot${i === active ? ' demo-dot--active' : ''}`} style={i === active ? { background: s.sportColor } : undefined} />
                  ))}
                </div>
              </div>

              {/* Tracker card floats off the frame's corner for depth */}
              <div className={`hud-card demo-float${switching ? ' hud--exit' : ''}`}>
                {isParlay ? (
                  <>
                    <div className="hud-card__label">
                      Parlay Tracker · {g.parlayLegs.filter(l => l.status === 'win').length}/{g.parlayLegs.length} legs hit
                    </div>
                    <div className="hud-parlay">
                      {g.parlayLegs.map((leg, i) => (
                        <div key={i} className={`hud-parlay__leg hud-parlay__leg--${leg.status}`}>
                          {leg.status === 'win' ? <Check size={11} weight="bold" /> : <span className="pulse-dot" />}
                          {leg.label}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="hud-card__label">{g.tracker.label}</div>
                    <div className="hud-tracker">
                      <div className="hud-tracker__info">
                        <span className="hud-tracker__name">{g.tracker.name}</span>
                        <span className="hud-tracker__detail">{g.tracker.detail}</span>
                      </div>
                      <span className="hud-tracker__stat" style={{ color: g.tracker.statColor }}>{g.tracker.stat}</span>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.div className="hero__scroll-arrow" aria-hidden="true" {...enter(1.4)}>
        <CaretDown size={28} />
      </motion.div>
    </section>
  );
}

// ── How It Works: pinned crossfade storytelling ─────────────
const HIW_STEPS = [
  {
    icon: <PlugsConnected size={18} />,
    title: 'Import your leagues',
    desc: 'Connect your ESPN and Sleeper fantasy leagues, with more platforms coming soon. MyZone pulls in every roster and matchup automatically.',
  },
  {
    icon: <Receipt size={18} />,
    title: 'Add your bet slips',
    desc: 'Drop in your active bets and parlays. We track every player prop, game line, and outcome that affects your action.',
  },
  {
    icon: <Play size={18} />,
    title: 'Watch live, automatically',
    desc: 'Our algorithm monitors every game in real time and switches your feed the moment something happens for your players and teams.',
  },
];

function HiwVisual({ step }) {
  if (step === 0) {
    return (
      <div className="hiw-visual">
        <div className="hiw-visual__glow hiw-visual__glow--red" aria-hidden="true" />
        <div className="hiw-platforms">
          <div className="hiw-platform">
            <span className="hiw-platform__name">ESPN Fantasy</span>
            <span className="hiw-platform__status hiw-platform__status--ok"><Check size={11} weight="bold" /> Connected</span>
          </div>
          <div className="hiw-platform">
            <span className="hiw-platform__name">Sleeper</span>
            <span className="hiw-platform__status hiw-platform__status--ok"><Check size={11} weight="bold" /> Connected</span>
          </div>
          <div className="hiw-platform hiw-platform--soon">
            <span className="hiw-platform__name">Yahoo Fantasy</span>
            <span className="hiw-platform__status">Coming soon</span>
          </div>
        </div>
        <p className="hiw-visual__note">Rosters and matchups sync automatically every game week.</p>
      </div>
    );
  }
  if (step === 1) {
    return (
      <div className="hiw-visual">
        <div className="hiw-visual__glow hiw-visual__glow--gold" aria-hidden="true" />
        <div className="hiw-slip">
          <div className="hiw-slip__leg hiw-slip__leg--win"><Check size={12} weight="bold" /> Chiefs ML</div>
          <div className="hiw-slip__leg hiw-slip__leg--win"><Check size={12} weight="bold" /> LeBron 25+ pts</div>
          <div className="hiw-slip__leg hiw-slip__leg--live"><span className="pulse-dot" /> Eagles -2.5</div>
        </div>
        <p className="hiw-visual__note">Every prop and line tracked against the live play-by-play.</p>
      </div>
    );
  }
  return (
    <div className="hiw-visual hiw-visual--video">
      <video
        src={eaglesVid}
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        className="hiw-visual__video"
      />
      <div className="hiw-visual__scrim" aria-hidden="true" />
      <div className="hud-pill hiw-visual__pill">
        <ArrowRight size={11} weight="bold" />
        Auto-switching
      </div>
      <p className="hiw-visual__note hiw-visual__note--overlay">Your feed jumps to the moment that matters. No remote required.</p>
    </div>
  );
}

function HiwSlide({ index }) {
  const s = HIW_STEPS[index];
  return (
    <div className={`hiw__slide-inner${index % 2 === 1 ? ' hiw__slide-inner--flip' : ''}`}>
      <div className="hiw__slide-copy">
        <span className="hiw__slide-num" aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
        <h3 className="hiw__slide-title">{s.icon}{s.title}</h3>
        <p className="hiw__slide-desc">{s.desc}</p>
      </div>
      <div className="hiw__slide-visual">
        <HiwVisual step={index} />
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <section id="how-it-works" className="section hiw">
      <div className="section__inner">
        <Reveal className="section__header section__header--left">
          <span className="section__label">How It Works</span>
          <h2 className="section__title">Three steps to your <span className="gradient-text">perfect gameday</span></h2>
        </Reveal>
        <div className="hiw__stack">
          {HIW_STEPS.map((_, i) => (
            <Reveal key={i}>
              <HiwSlide index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Features: asymmetric bento with cursor spotlight ────────
function Features() {
  const onMove = (e) => {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    const cell = e.currentTarget;
    const r = cell.getBoundingClientRect();
    cell.style.setProperty('--mx', `${e.clientX - r.left}px`);
    cell.style.setProperty('--my', `${e.clientY - r.top}px`);
  };

  const cells = [
    {
      area: 'a',
      icon: <Lightning size={20} weight="fill" />,
      title: 'Catch every swing moment',
      desc: 'Our algorithm processes live play-by-play data and switches your feed within seconds of a key moment.',
      visual: 'pulse',
    },
    {
      area: 'b',
      icon: <PlugsConnected size={20} />,
      title: 'Works with apps you already use',
      desc: 'Connect ESPN, Sleeper, Kalshi, and Polymarket in seconds. Sportsbook slip import is on the way.',
      visual: 'chips',
      chips: ['ESPN', 'Sleeper', 'Kalshi', 'Polymarket'],
    },
    {
      area: 'c',
      icon: <SquaresFour size={20} />,
      title: 'Multi-sport by design',
      desc: 'NFL, NBA, and MLB are live, with more sports on the way, across every league and season.',
      visual: 'leagues',
    },
    {
      area: 'd',
      icon: <UserFocus size={20} />,
      title: 'Your zone, your rules',
      desc: 'Not RedZone. Not a highlights reel. Every play you care about, live and in order of importance.',
      visual: 'wash',
    },
    {
      area: 'e',
      icon: <ChartLineUp size={20} />,
      title: 'Live stats & alerts',
      desc: 'Real-time stats, scoring projections, and push alerts keep you ahead of your league all game long.',
      visual: 'bars',
    },
    {
      area: 'f',
      icon: <ShieldCheck size={20} />,
      title: 'Privacy first',
      desc: 'Your data stays yours. We never sell your information or share it with third parties.',
    },
  ];

  return (
    <section id="features" className="section features">
      <div className="section__inner">
        <Reveal className="section__header section__header--left">
          <h2 className="section__title">Engineered for <span className="gradient-text">gameday</span></h2>
        </Reveal>

        <div className="bento">
          {cells.map((c, i) => (
            <Reveal key={c.area} delay={i * 0.05} className={`bento__wrap bento__wrap--${c.area}`}>
              <div className="bento__cell" onPointerMove={onMove}>
                {c.visual === 'pulse' && (
                  <div className="bento-pulse" aria-hidden="true">
                    <span /><span /><span />
                  </div>
                )}
                {c.visual === 'wash' && <div className="bento-wash" aria-hidden="true" />}
                {c.visual === 'bars' && (
                  <div className="bento-bars" aria-hidden="true">
                    {[...Array(12)].map((_, bi) => <span key={bi} style={{ '--i': bi }} />)}
                  </div>
                )}
                {c.visual === 'leagues' && (
                  <div className="bento-leagues" aria-hidden="true">
                    <span className="bento-leagues__live">NFL</span>
                    <span className="bento-leagues__live">NBA</span>
                    <span className="bento-leagues__live">MLB</span>
                    <span>NHL</span>
                  </div>
                )}
                <div className="bento__content">
                  <div className="bento__icon">{c.icon}</div>
                  <h3 className="bento__title">{c.title}</h3>
                  <p className="bento__desc">{c.desc}</p>
                  {c.visual === 'chips' && (
                    <div className="bento-chips">
                      {c.chips.map(ch => <span key={ch}>{ch}</span>)}
                    </div>
                  )}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Press ───────────────────────────────────────────────────
function Press() {
  return (
    <section id="press" className="section press">
      <div className="section__inner">
        <Reveal>
          <a
            href="https://sportstechtoday.com/filling-hole-fantasy-football-with-myzone/"
            target="_blank"
            rel="noopener noreferrer"
            className="press-feature"
          >
            <span className="press-feature__source">Sports Tech Today</span>
            <span className="press-feature__quote">
              {'“'}Filling the Hole in Fantasy Football with MyZone{'”'}
            </span>
            <span className="press-feature__sub">
              A deep dive into how MyZone Sports is solving the fragmented fantasy viewing experience.
            </span>
            <span className="press-feature__cta">
              Read the article
              <ArrowUpRight size={16} weight="bold" />
            </span>
          </a>
        </Reveal>
      </div>
    </section>
  );
}

// ── Our Story ───────────────────────────────────────────────
function OurStory() {
  const [expanded, setExpanded] = useState(false);

  return (
    <section id="our-story" className="section our-story">
      <div className="section__inner our-story__inner">
        <Reveal className="our-story__text">
          <span className="section__label">Our Story</span>
          <h2 className="section__title">Built by fans, <span className="gradient-text">for fans</span></h2>
          <p className="our-story__body">
            Founder Ryed Badr has been playing fantasy football since the age of eight.
            Frustrated that he could never watch all his players live since multiple games aired simultaneously,
            he relied on RedZone to fill the gaps. Still, it wasn't enough. RedZone wouldn't always show
            what he was interested in seeing.
          </p>
          {expanded && (
            <p className="our-story__body">
              Years later, with a background in software engineering and alongside co-founders Michael Strobl,
              Vinay Krishnan, and Aman Shah, Ryed set out to solve that problem. Together they are building
              MyZone Sports: a platform that lets users import their fantasy leagues and bet slips into one
              central hub. Their algorithms predict which games you want to see and switch between them live,
              acting as your own personalized RedZone.
            </p>
          )}
          <button className="btn-ghost" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show Less' : 'Read Full Story'}
          </button>
        </Reveal>
        <div className="our-story__team">
          <div className="team-grid">
            {[
              { name: 'Ryed Badr',      img: '/ryedbadr.jpeg',        linkedin: 'https://www.linkedin.com/in/ryed-badr/',              role: 'Founder, CEO' },
              { name: 'Michael Strobl', img: '/michaelstrobl.jpeg',   linkedin: 'https://www.linkedin.com/in/michaelcstrobl/',          role: 'Founder, COO' },
              { name: 'Vinay Krishnan', img: '/vinaykrishnan.jpeg',   linkedin: 'https://www.linkedin.com/in/vinay-krishnan-eashan/',   role: 'Founder, CTO' },
              { name: 'Aman Shah',      img: '/amanshah.jpeg',        linkedin: 'https://www.linkedin.com/in/amanshah9/',               role: 'Founder, CPO' },
            ].map(({ name, img, linkedin, role }, i) => (
              <Reveal key={name} delay={i * 0.07}>
                <a className="team-card" href={linkedin} target="_blank" rel="noreferrer">
                  <div className="team-card__avatar">
                    <img src={img} alt={name} loading="lazy" />
                  </div>
                  <p className="team-card__name">{name}</p>
                  <p className="team-card__role">{role}</p>
                  <ArrowUpRight size={14} weight="bold" className="team-card__arrow" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Bottom CTA with signup count-up ─────────────────────────
function LaunchCTA() {
  const reduce = useReducedMotion();
  const countRef = useRef(null);
  const inView = useInView(countRef, { once: true, margin: '-80px' });
  const count = useMotionValue(0);
  const rounded = useTransform(count, v => `${Math.round(v).toLocaleString()}+`);

  useEffect(() => {
    if (!inView) return;
    if (reduce) {
      count.set(2400);
      return;
    }
    const controls = animate(count, 2400, { duration: 1.6, ease: 'easeOut' });
    return () => controls.stop();
  }, [inView, reduce, count]);

  return (
    <section id="launch-cta" className="section launch-cta">
      <div className="launch-cta__bg" aria-hidden="true">
        <div className="aurora aurora-cta" />
        <div className="hero-grid" />
      </div>
      <div className="section__inner launch-cta__inner">
        <Reveal>
          <h2 className="launch-cta__title">Ready to watch <span className="gradient-text">your zone?</span></h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="launch-cta__sub" ref={countRef}>
            Join <motion.span className="launch-cta__count">{rounded}</motion.span> fantasy players and bettors
            experiencing gameday the right way.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <LaunchAppButton className="btn-launch--cta" />
        </Reveal>
      </div>
    </section>
  );
}

// ── Footer ──────────────────────────────────────────────────
function Footer() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="footer">
      <div className="footer__inner">
        <div className="footer__brand">
          <div className="footer__logo">
            <img src="/MyZone.PNG" alt="MyZone Sports" className="footer__logo-img" />
            <span className="footer__logo-text">MyZone <span className="footer__logo-accent">Sports</span></span>
          </div>
          <p className="footer__tagline">Your fantasy. Your bets. One live feed.</p>
        </div>

        <nav className="footer__nav">
          <div className="footer__nav-col">
            <p className="footer__nav-heading">Product</p>
            <a href={APP_URL} target="_blank" rel="noopener noreferrer">Launch the App</a>
            <button onClick={() => scrollTo('how-it-works')}>How It Works</button>
            <button onClick={() => scrollTo('features')}>Features</button>
          </div>
          <div className="footer__nav-col">
            <p className="footer__nav-heading">Company</p>
            <button onClick={() => scrollTo('our-story')}>Our Story</button>
            <button onClick={() => scrollTo('press')}>Press</button>
          </div>
        </nav>

        <div className="footer__social">
          <p className="footer__nav-heading">Follow Us</p>
          <div className="footer__social-links">
            <a href="https://www.instagram.com/myzonefootball" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.69.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.322a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
              </svg>
            </a>
            <a href="https://x.com/MyZoneFootball" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.207-6.807-5.974 6.807H2.882l7.705-8.816L1.254 2.25h6.554l4.713 6.231 5.42-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117Z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
      <div className="footer__bottom">
        <p>© {new Date().getFullYear()} MyZone Sports. All rights reserved.</p>
      </div>
      <div className="footer__wordmark" aria-hidden="true">MYZONE</div>
    </footer>
  );
}

// ── Main App ────────────────────────────────────────────────
function App() {
  return (
    <div className="app">
      <div className="grain" aria-hidden="true" />
      <Navbar />
      <main>
        <Hero />
        <HowItWorks />
        <Features />
        <Press />
        <OurStory />
        <LaunchCTA />
      </main>
      <Footer />
    </div>
  );
}

export default App;
